import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isPublicMoorageListing } from '../../util/listingHelpers';
import { getOpenOrderBarHeight } from '../../components/OrderPanel/orderPanelLayout';

import PublicMoorageActions from './PublicMoorageActions';

import css from './ListingPage.module.css';

const PUBLIC_MOORAGE_ACTIONS_VISIBLE_MEDIA_QUERY = '(max-width: 1023px)';
const ORDER_PANEL_OFFSET_CSS_VAR = '--orderPanelOpenBarOffsetMobile';
const ORDER_PANEL_HEIGHT_CSS_VAR = '--orderPanelOpenBarHeight';

export const formatPublicMoorageExpiresAt = (intl, checkIn) => {
  const expiresAt = checkIn?.expiresAt ? new Date(checkIn.expiresAt) : null;

  if (!expiresAt || Number.isNaN(expiresAt.getTime())) {
    return '';
  }

  const timeZone = checkIn.timeZone || 'Etc/UTC';
  const date = intl.formatDate(expiresAt, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone,
  });
  const time = intl.formatTime(expiresAt, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  });

  return intl.formatMessage({ id: 'ListingPage.publicMoorageExpiresAt' }, { date, time });
};

const useMobilePublicMoorageActionLayout = enabled => {
  const actionPanelRef = useRef(null);

  useEffect(() => {
    if (
      !enabled ||
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return undefined;
    }

    const body = document.body;
    const documentElement = document.documentElement;
    const mediaQuery = window.matchMedia(PUBLIC_MOORAGE_ACTIONS_VISIBLE_MEDIA_QUERY);
    let resizeObserver = null;

    const clearLayout = () => {
      body.classList.remove('has-open-orderpanel');
      documentElement.style.removeProperty(ORDER_PANEL_OFFSET_CSS_VAR);
      documentElement.style.removeProperty(ORDER_PANEL_HEIGHT_CSS_VAR);
    };

    const syncPublicMoorageActionLayout = () => {
      const isMobileViewport = mediaQuery.matches;
      body.classList.toggle('has-open-orderpanel', isMobileViewport);

      if (!isMobileViewport) {
        documentElement.style.removeProperty(ORDER_PANEL_OFFSET_CSS_VAR);
        documentElement.style.removeProperty(ORDER_PANEL_HEIGHT_CSS_VAR);
        return;
      }

      const measuredHeight = getOpenOrderBarHeight(actionPanelRef.current);
      if (measuredHeight > 0) {
        const pxHeight = `${measuredHeight}px`;
        documentElement.style.setProperty(ORDER_PANEL_OFFSET_CSS_VAR, pxHeight);
        documentElement.style.setProperty(ORDER_PANEL_HEIGHT_CSS_VAR, pxHeight);
      }
    };

    syncPublicMoorageActionLayout();

    if (typeof window.ResizeObserver !== 'undefined' && actionPanelRef.current) {
      resizeObserver = new window.ResizeObserver(() => {
        syncPublicMoorageActionLayout();
      });
      resizeObserver.observe(actionPanelRef.current);
    }

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', syncPublicMoorageActionLayout);
    } else {
      mediaQuery.addListener(syncPublicMoorageActionLayout);
    }
    window.addEventListener('resize', syncPublicMoorageActionLayout);

    return () => {
      clearLayout();
      window.removeEventListener('resize', syncPublicMoorageActionLayout);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', syncPublicMoorageActionLayout);
      } else {
        mediaQuery.removeListener(syncPublicMoorageActionLayout);
      }
    };
  }, [enabled]);

  return actionPanelRef;
};

const PublicMoorageOccupiedState = props => {
  const { publicData, activeCheckIn, activeCheckInAge } = props;
  const intl = useIntl();
  const boatName = activeCheckIn?.boatName || activeCheckIn?.boat?.name;
  const expiresAt = formatPublicMoorageExpiresAt(intl, activeCheckIn);

  return (
    <>
      <div className={css.publicMoorageOccupiedButton}>
        <span>
          <FormattedMessage id="ListingPage.publicMoorageOccupied" />
        </span>
        <small>
          <FormattedMessage
            id="ListingPage.publicMoorageCheckedInAgo"
            values={{ timeAgo: activeCheckInAge || '0 min' }}
          />
        </small>
      </div>
      <div className={css.publicMoorageDetailsTable}>
        {boatName ? (
          <div className={css.publicMoorageDetailRow}>
            <span>
              <FormattedMessage id="ListingPage.publicMoorageBoat" />
            </span>
            <strong>{boatName}</strong>
          </div>
        ) : null}
        {expiresAt ? (
          <div className={css.publicMoorageDetailRow}>
            <span>
              <FormattedMessage id="ListingPage.publicMoorageExpires" />
            </span>
            <strong>{expiresAt}</strong>
          </div>
        ) : null}
      </div>
      <p className={css.publicMoorageOccupiedCopy}>
        <FormattedMessage id="ListingPage.publicMoorageAdditionalBoats" />
        <br />
        <br />
        <FormattedMessage id="ListingPage.publicMoorageOccupiedCommunity" />
        <br />
        <br />
        <FormattedMessage id="ListingPage.publicMoorageConfirmAdditionalSpace" />
      </p>
      <PublicMoorageActions publicData={publicData} showCheckIn={false} />
    </>
  );
};

const PublicMoorageActionSection = props => {
  const { publicData = {}, activeCheckIn, activeCheckInAge, onCheckIn } = props;
  const isPublicMoorage = isPublicMoorageListing(publicData);
  const isAvailableForCheckIn = isPublicMoorage && !activeCheckIn;
  const actionPanelRef = useMobilePublicMoorageActionLayout(isAvailableForCheckIn);

  if (!isPublicMoorage) {
    return null;
  }

  return (
    <div
      className={classNames(css.publicMooragePanel, {
        [css.publicMooragePanelAvailable]: isAvailableForCheckIn,
      })}
      ref={isAvailableForCheckIn ? actionPanelRef : null}
    >
      {activeCheckIn ? (
        <PublicMoorageOccupiedState
          publicData={publicData}
          activeCheckIn={activeCheckIn}
          activeCheckInAge={activeCheckInAge}
        />
      ) : (
        <PublicMoorageActions publicData={publicData} onCheckIn={onCheckIn} />
      )}
    </div>
  );
};

export default PublicMoorageActionSection;
